/**
 * Sprint 25.3 — RecentlyTimeline (brain snapshot §8).
 *
 * A prose-style timeline of recent activity across the agent. Workflow
 * transitions (the locked vocabulary in Decisions §8) are derived programmatically
 * via `describeRecentEvent`; Sprint 26.1 interleaves **routine runs** (Activity
 * rows tagged with `routine_ref`) into the same timeline, sorted by time. Primary
 * nouns are emphasised. `describeRecentEvent` + `relativeTime` are pure and
 * unit-tested in scripts/test-brain-snapshot-25.3.ts.
 */

import { Fragment, type CSSProperties } from "react";
import type { RecentEvent } from "../../../lib/types";
import { describeRecentEvent, relativeTime } from "../../lib/describe-recent-event";
import { collapseRecentDuplicates } from "../../lib/agent-page-25.6";
import type { RoutineRunLine } from "../../lib/routine-run";

export interface RecentlyTimelineProps {
  recent: RecentEvent[];
  /** Sprint 26.1 — routine runs to interleave (Activity rows with routine_ref). */
  routineRuns?: RoutineRunLine[];
  agentName: string;
}

const MAX_ROWS = 6;

type Row =
  | { key: string; occurred_at: string; kind: "transition"; event: RecentEvent; count: number }
  | { key: string; occurred_at: string; kind: "run"; run: RoutineRunLine };

export function RecentlyTimeline({ recent, routineRuns = [], agentName }: RecentlyTimelineProps) {
  // Sprint 25.6 — collapse consecutive identical transitions into one row with a
  // "· ×n" count (Decision §7). Sprint 26.1 — merge routine runs in by time.
  const transitionRows: Row[] = collapseRecentDuplicates(recent).map(({ event, count }) => ({
    key: `t:${event.transition_id}`,
    occurred_at: event.occurred_at,
    kind: "transition",
    event,
    count,
  }));
  const runRows: Row[] = routineRuns.map((run) => ({
    key: `r:${run.id}`,
    occurred_at: run.occurred_at,
    kind: "run",
    run,
  }));

  const rows = [...transitionRows, ...runRows]
    .sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : a.occurred_at > b.occurred_at ? -1 : 0))
    .slice(0, MAX_ROWS);

  return (
    <section style={{ marginBottom: 22 }} data-testid="recently-timeline">
      <h3 style={header}>Recently</h3>
      {rows.length === 0 ? (
        <div style={empty}>{agentName} hasn't recorded recent activity.</div>
      ) : (
        <div style={container}>
          {rows.map((row) => (
            <div key={row.key} style={rowStyle} data-testid={row.kind === "run" ? "recently-routine-run" : undefined}>
              <span style={time}>{relativeTime(row.occurred_at)}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                {row.kind === "transition" ? (
                  <>
                    {describeRecentEvent(row.event).map((seg, i) => (
                      <Fragment key={i}>
                        {seg.strong ? <strong style={strong}>{seg.text}</strong> : seg.text}
                      </Fragment>
                    ))}
                    {row.count > 1 && <span style={countBadge}> · ×{row.count}</span>}
                  </>
                ) : (
                  <>
                    <strong style={strong}>{row.run.label}</strong>
                    {row.run.summary && <span> · {row.run.summary}</span>}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const header: CSSProperties = {
  margin: "0 0 10px",
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: "0.4px",
  color: "var(--color-text-secondary)",
};

const container: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  lineHeight: 1.7,
};

const rowStyle: CSSProperties = {
  display: "flex",
  gap: 12,
  padding: "4px 0",
};

const time: CSSProperties = {
  color: "var(--color-text-tertiary)",
  width: 70,
  flexShrink: 0,
};

const strong: CSSProperties = {
  color: "var(--color-text-primary)",
  fontWeight: 500,
};

const countBadge: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const empty: CSSProperties = {
  fontSize: 12,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
};
